import {AfterViewInit, Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {LoaderService} from '../../../../../../services/loader/loader.service';
import {FunnelService} from '../../../../../../services/cms/funnel.service';
import {IsGranted} from '../../../../../../decorator/is-granted';
import {MetaTitle} from '../../../../../../decorator/meta-title';
import {Funnel} from '../../../../../../models/cms/funnel/funnel';
import {FunnelPage} from '../../../../../../models/cms/funnel/funnel-page';
import {NgClass, NgStyle} from '@angular/common';
import {StaticPageService} from '../../../../../../services/cms/static-page.service';
import {forkJoin} from 'rxjs';
import {StaticPage} from '../../../../../../models/cms/static-page';
import {PageType} from '../../../../../../models/page-type';
import {PageTypeService} from '../../../../../../services/page-type/page-type.service';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {AvailableDomainService} from '../../../../../../services/cms/available-domain.service';
import {AvailableDomain} from '../../../../../../models/cms/available-domain';
import {Product} from '../../../../../../models/product';
import {ProductService} from '../../../../../../services/product/product.service';
import {InputSwitchComponent} from '../../../../../layout/input-switch/input-switch.component';
import {SnackbarService} from '../../../../../../services/snackbar/snackbar.service';
import {ConfirmDirective} from '../../../../../../directives/confirm/confirm.directive';
import {FunnelPageType} from '../../../../../../constants/funnel-page-type';
import {DialogService} from '../../../../../../services/dialog/dialog.service';
import {FunnelUrlEditorComponent} from './funnel-url-editor/funnel-url-editor.component';
import {FunnelLink} from '../../../../../../models/cms/funnel/funnel-link';
import {FunnelLinkCondition} from '../../../../../../models/cms/funnel/funnel-link-condition';
import {FunnelLinkConditionType} from '../../../../../../constants/funnel-link-condition-type';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'app-funnel-form',
  imports: [
    RouterLink,
    NgStyle,
    NgClass,
    ReactiveFormsModule,
    InputSwitchComponent,
    ConfirmDirective,
    FormsModule,
    TranslatePipe
  ],
  templateUrl: './funnel-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './funnel-form.component.scss'
})
@IsGranted(['FUNNEL_VIEW'])
@MetaTitle('Funnel Form')
export class FunnelFormComponent implements OnInit, AfterViewInit {
  static readonly FUNNEL_PAGE_WIDTH = 300;
  static readonly FUNNEL_PAGE_HEIGHT = 120;
  static readonly GRID_SIZE = 20;

  tmpPageId = -1;
  loaded = false;
  id: number | null = null;
  funnel: Funnel | null = null;
  pages: FunnelPage[] = [];
  links: FunnelFormLink[] = [];
  pageTypes: PageType[] = [];
  staticPages: StaticPage[] = [];
  availableDomains: AvailableDomain[] = [];
  availableDomainLoading = false;
  products: Product[] = [];
  availableLocales: string[] = [];
  selectedPage: FunnelPage | null = null;
  selectedLink: FunnelFormLink | null = null;

  movingPage: FunnelPage | null = null;
  movingPageStartX = 0;
  movingPageStartY = 0;

  movingLink: FunnelFormLink | null = null;
  movingLinkStartX = 0;
  movingLinkStartY = 0;

  tabTools = 'parameters';

  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    productId: new FormControl<number | null>(null),
    lang: new FormControl<string | null>(null),
    slug: new FormControl('', [Validators.required]),
    domainId: new FormControl<number | null>(null, [Validators.required]),
    startPage: new FormControl<FunnelPage | null>(null, [Validators.required]),
    enabled: new FormControl<boolean>(true),
  });

  linkConditionType: FunnelLinkConditionType | null = null;
  linkConditionTypes = [
    {id: FunnelLinkConditionType.LEAD_STATUS},
  ];

  @ViewChild('funnelContainer') funnelContainer: ElementRef | null = null;
  @ViewChild('toolsTabProperties') toolsTabProperties: ElementRef | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private funnelService: FunnelService,
    private staticPageService: StaticPageService,
    private pageTypeService: PageTypeService,
    private availableDomainService: AvailableDomainService,
    private productService: ProductService,
    private dialogService: DialogService,
  ) {
  }

  ngOnInit(): void {
    this.id = Number(this.route.snapshot.paramMap.get('id'));

    this.loader.startLoading();

    forkJoin([
      this.pageTypeService.findAll({sort: 'name'}),
      this.staticPageService.findAll({sort: 'name'}),
      this.productService.findAll({sort: 'name'}),
    ]).subscribe(([pageTypes, staticPages, products]) => {
      this.pageTypes = pageTypes;
      this.staticPages = staticPages;
      this.products = products;

      this.refresh();
    });
  }

  ngAfterViewInit(): void {
  }

  isCreate(): boolean {
    return !this.id;
  }

  refresh(): void {
    if (this.id) {
      const params: any = {groups: 'pages,links,staticPage'};

      this.funnelService.findOneById(this.id, params).subscribe(funnel => {
        this.funnel = funnel;

        this.pages = [];
        this.links = [];

        // Copy pages
        this.funnel.pages.forEach(pageData => {
          const page = new FunnelPage().deserialize(pageData);

          // Association current static page instance
          const staticPageId = pageData.staticPage?.id;
          if (staticPageId) {
            const staticPage = this.staticPages.find(currentPage => currentPage.id === staticPageId);
            if (staticPage) {
              page.staticPage = staticPage;
            }
          }

          this.pages.push(page);
        });

        // Resolve and copy links
        this.pages.forEach(page => {
          page.links.forEach(link => {
            if (link.toId) {
              const nextPage = this.getPageById(link.toId);
              if (nextPage) {
                this.links.push({
                  previousPage: page,
                  nextPage: nextPage,
                  link: link
                });
              }
            }
          });
        });

        // Resolve start page
        const startPage = this.funnel.startPageId ? this.getPageById(this.funnel.startPageId) : null;

        // Patch form (pre-fill the brand/lang filters from the funnel's current domain)
        this.form.patchValue({
          name: this.funnel.name,
          productId: this.funnel.domain?.product?.id ?? null,
          lang: this.funnel.domain?.lang ?? null,
          slug: this.funnel.slug,
          domainId: this.funnel.domain?.id ?? null,
          startPage: startPage,
          enabled: this.funnel.enabled,
        });

        // Initialize the brand -> lang -> domain cascade from the patched values
        this.refreshLocales();

        // requestAnimationFrame(() => {
        //   this.organize();
        // });

        this.loader.stopLoading();
        this.loaded = true;
      });
    } else {
      // No brand selected yet: lang and domain start disabled
      this.refreshLocales();

      this.loader.stopLoading();
      this.loaded = true;
    }
  }

  getPageById(id: number): FunnelPage | null {
    return this.pages.find((page: FunnelPage) => page.id === id) ?? null;
  }

  getSelectedProduct(): Product | null {
    const productId = this.form.get('productId')?.value ?? null;

    if (productId) {
      return this.products.find((product: Product) => product.id === productId) ?? null;
    }

    return null;
  }

  getSelectedLang(): string | null {
    return this.form.get('lang')?.value ?? null;
  }

  refreshLocales(): void {
    const product = this.getSelectedProduct();
    this.availableLocales = product?.languages ?? [];

    if (this.availableLocales.length) {
      this.form.get('lang')?.enable();
    } else {
      this.form.get('lang')?.disable();
      this.form.get('lang')?.setValue(null);
    }

    this.refreshDomains();
  }

  refreshDomains(): void {
    this.availableDomains = [];

    const productId = this.getSelectedProduct()?.id ?? null;
    const lang = this.getSelectedLang();

    if (productId === null) {
      this.form.get('domainId')?.disable();
      this.form.get('domainId')?.setValue(null);
      return;
    }

    this.form.get('domainId')?.enable();

    const params = {
      sort: 'domain',
      productId: productId,
      lang: lang
    };

    this.availableDomainLoading = true;
    this.availableDomainService.findAll(params).subscribe((availableDomains: AvailableDomain[]) => {
      this.availableDomains = availableDomains;
      this.availableDomainLoading = false;
    });
  }

  getFunnelStyle(): any {
    let width = 0;
    let height = 0;

    this.pages.forEach(page => {
      width = Math.max(width, page.positionX ?? 0);
      height = Math.max(height, page.positionY ?? 0);
    });

    width += FunnelFormComponent.FUNNEL_PAGE_WIDTH + 50;
    height += FunnelFormComponent.FUNNEL_PAGE_HEIGHT + 100;

    return {
      'width': width + 'px',
      'height': height + 'px',
      'background-size': FunnelFormComponent.GRID_SIZE + 'px ' + FunnelFormComponent.GRID_SIZE + 'px '
    }
  }

  getPageStyle(page: FunnelPage): any {
    return {
      width: FunnelFormComponent.FUNNEL_PAGE_WIDTH + 'px',
      height: FunnelFormComponent.FUNNEL_PAGE_HEIGHT + 'px',
      'margin-left': Math.round(-FunnelFormComponent.FUNNEL_PAGE_WIDTH / 2) + 'px',
      left: page.positionX + 'px',
      top: page.positionY + 'px'
    }
  }

  isRegisterLead(page: FunnelPage): boolean {
    return page.options?.['registerLead'] !== false;
  }

  setRegisterLead(page: FunnelPage, registerLead: boolean): void {
    page.options ??= {};
    page.options['registerLead'] = registerLead;
  }

  getPageClass(page: FunnelPage): any {
    const classes = [
      'type-' + page.type
    ];

    if (this.isStartPage(page)) {
      classes.push('start-page');
    }

    if (this.isSelectedPage(page)) {
      classes.push('selected-page');
    }

    return classes;
  }

  /**
   * Draw the link curve
   */
  getLinkSvgPath(link: FunnelFormLink): string {
    let path = '';

    const pageHeight = FunnelFormComponent.FUNNEL_PAGE_HEIGHT;

    const previousPageX = link.previousPage?.positionX ?? 0;
    const previousPageY = link.previousPage?.positionY ?? 0;

    let nextPageX;
    let nextPageY;

    if (link.nextPage) {
      nextPageX = link.nextPage?.positionX ?? 0;
      nextPageY = link.nextPage?.positionY ?? 0;
    } else {
      nextPageX = link.nextX ?? 0;
      nextPageY = link.nextY ?? 0;
    }

    if (!nextPageX || !nextPageY) {
      return '';
    }

    const offsetY = 20;
    const startY = previousPageY + pageHeight;
    const middleX = (previousPageX + nextPageX) / 2;
    const middleY = (startY + nextPageY) / 2;
    const startOffsetY = startY + offsetY;
    const destOffsetY = nextPageY - offsetY;

    path += 'M ' + previousPageX + ' ' + startY;
    path += ' L ' + previousPageX + ' ' + startOffsetY;

    if (startOffsetY < destOffsetY) {
      path += ' C ' + previousPageX + ' ' + middleY;
      path += ' ' + nextPageX + ' ' + middleY;
      path += ' ' + nextPageX + ' ' + destOffsetY;
    } else {
      path += ' C ' + middleX + ' ' + startOffsetY;
      path += ' ' + middleX + ' ' + destOffsetY;
      path += ' ' + nextPageX + ' ' + destOffsetY;
    }

    path += ' L ' + nextPageX + ' ' + nextPageY;

    return path;
  }

  /**
   * Draw the link arrow
   */
  getLinkSvgArrowPath(link: FunnelFormLink): string {
    let path = '';

    let nextPageX;
    let nextPageY;

    if (link.nextPage) {
      nextPageX = link.nextPage?.positionX ?? 0;
      nextPageY = link.nextPage?.positionY ?? 0;
    } else {
      nextPageX = link.nextX ?? 0;
      nextPageY = link.nextY ?? 0;
    }

    if (!nextPageX || !nextPageY) {
      return '';
    }

    const size = 10;

    path += 'M ' + nextPageX + ' ' + nextPageY;
    path += ' L ' + (nextPageX - size) + ' ' + (nextPageY - size);
    path += ' L ' + (nextPageX + size) + ' ' + (nextPageY - size);

    return path;
  }

  startMovingPage(e: MouseEvent, page: FunnelPage): void {
    if (page) {
      if (this.movingLink) {
        /**
         * New link mode
         */
        if (this.movingLink.previousPage) {
          this.createLink(this.movingLink.previousPage, page);
        }
      } else {
        /**
         * Start moving page
         */
        const posX = page.positionX ?? FunnelFormComponent.FUNNEL_PAGE_WIDTH;
        const posY = page.positionY ?? FunnelFormComponent.FUNNEL_PAGE_HEIGHT;

        this.movingPage = page;
        this.movingPageStartX = posX - e.clientX;
        this.movingPageStartY = posY - e.clientY;

        this.selectPage(page);
      }

      e.stopPropagation();
    }
  }

  startLinkPage(e: MouseEvent, page: FunnelPage): void {
    if (page) {
      /**
       * Start creating link
       */
      this.movingLink = {
        previousPage: page
      };
      const posX = page.positionX ?? FunnelFormComponent.FUNNEL_PAGE_WIDTH;
      const posY = page.positionY ?? FunnelFormComponent.FUNNEL_PAGE_HEIGHT;

      this.movingLinkStartX = posX - e.clientX;
      this.movingLinkStartY = posY + FunnelFormComponent.FUNNEL_PAGE_HEIGHT - e.clientY;
    }
  }

  stopMovingPage(): void {
    this.movingPage = null;
    this.movingLink = null;
  }

  clearSelection(): void {
    this.selectedPage = null;
    this.selectedLink = null;
  }

  move(e: MouseEvent): void {
    /**
     * Moving page
     */
    if (this.movingPage) {
      this.movingPage.positionX = e.clientX + this.movingPageStartX;
      this.movingPage.positionY = e.clientY + this.movingPageStartY;

      this.movingPage.positionX = Math.round(this.movingPage.positionX / FunnelFormComponent.GRID_SIZE) * FunnelFormComponent.GRID_SIZE;
      this.movingPage.positionY = Math.round(this.movingPage.positionY / FunnelFormComponent.GRID_SIZE) * FunnelFormComponent.GRID_SIZE;

      this.findFreePosition(this.movingPage);
    }

    /**
     * Creating new link
     */
    if (this.movingLink) {
      this.movingLink.nextX = e.clientX + this.movingLinkStartX;
      this.movingLink.nextY = e.clientY + this.movingLinkStartY;
    }
  }

  findFreePosition(page: FunnelPage): void {
    const gridSize = FunnelFormComponent.GRID_SIZE;
    const x = Math.round((page.positionX ?? 0) / FunnelFormComponent.GRID_SIZE) * FunnelFormComponent.GRID_SIZE;
    const y = Math.round((page.positionY ?? 0) / FunnelFormComponent.GRID_SIZE) * FunnelFormComponent.GRID_SIZE;

    let hitPage = null;
    let growSearch = 0;

    do {
      const minY = Math.max(y - growSearch, 0);
      for (let dy = minY; dy <= x + growSearch; dy += gridSize) {

        const minX = Math.max(x - growSearch, 0);
        for (let dx = minX; dx <= x + growSearch; dx += gridSize) {

          hitPage = this.pages.find(currentPage => {
            if (currentPage === page) {
              return false;
            }

            const currentPageX = currentPage.positionX ?? 0;
            const currentPageY = currentPage.positionY ?? 0;

            const distance = Math.sqrt((currentPageX - dx) ** 2 + (currentPageY - dy) ** 2);
            return distance < gridSize;
          });

          if (!hitPage) {
            page.positionX = dx;
            page.positionY = dy;
            return;
          }
        }
      }

      growSearch += gridSize;

    } while (true);
  }

  movePage(e: MouseEvent, page: FunnelPage): void {
    if (this.movingLink) {
      /**
       * Magnet the link to the destination page
       */
      const posX = page.positionX ?? FunnelFormComponent.FUNNEL_PAGE_WIDTH;
      const posY = page.positionY ?? FunnelFormComponent.FUNNEL_PAGE_HEIGHT;

      this.movingLink.nextX = posX;
      this.movingLink.nextY = posY;
      e.stopPropagation();
    }
  }

  getStaticPagesByType(pageType: PageType): StaticPage[] {
    return this.staticPages.filter((staticPage: StaticPage) => staticPage.pageType?.id === pageType.id);
  }

  createLink(fromPage: FunnelPage, toPage: FunnelPage): void {
    if (fromPage && toPage && fromPage !== toPage) {
      if (this.links.findIndex((link: FunnelFormLink) => link.previousPage === fromPage && link.nextPage === toPage) >= 0) {
        // Link already exists
        return;
      }

      // Add link
      this.links.push({
        previousPage: fromPage,
        nextPage: toPage,
        link: new FunnelLink()
      });
    }
  }

  deleteLink(link: FunnelFormLink): void {
    this.links.splice(this.links.indexOf(link), 1);
    this.clearSelection();
  }

  selectLink(e: Event, link: FunnelFormLink): void {
    this.clearSelection();
    this.selectedLink = link;
    this.tabTools = 'properties';
    e.stopPropagation();
  }

  isSelectedPage(page: FunnelPage): boolean {
    return this.selectedPage === page;
  }

  isStartPage(page: FunnelPage): boolean {
    return this.getStartPage() === page;
  }

  getStartPage(): FunnelPage | null {
    return this.form.get('startPage')?.value ?? null;
  }

  setStartPage(page: FunnelPage): void {
    this.form.get('startPage')?.setValue(page);
  }

  createPage(staticPage: StaticPage): void {
    const funnelPage = new FunnelPage();
    funnelPage.id = this.tmpPageId--;
    funnelPage.name = staticPage.name;
    funnelPage.type = FunnelPageType.STATIC_PAGE;
    funnelPage.slug = staticPage.slug;
    funnelPage.staticPage = staticPage;

    funnelPage.positionX = FunnelFormComponent.FUNNEL_PAGE_WIDTH / 2 + 20;
    funnelPage.positionY = 20;

    this.findFreePosition(funnelPage);

    this.pages.push(funnelPage);

    this.selectPage(funnelPage);
  }

  deletePage(page: FunnelPage): void {
    // Delete page
    this.pages.splice(this.pages.indexOf(page), 1);

    // Delete all links
    this.links.filter((link: FunnelFormLink) => {
      return link.previousPage === page || link.nextPage === page;
    }).forEach((link: FunnelFormLink) => {
      this.links.splice(this.links.indexOf(link), 1);
    });
  }

  selectPage(page: FunnelPage): void {
    this.clearSelection();
    this.selectedPage = page;
    this.tabTools = 'properties';
  }

  createExternalLink(): void {
    const funnelPage = new FunnelPage();
    funnelPage.id = this.tmpPageId--;
    funnelPage.name = 'New external link';
    funnelPage.type = FunnelPageType.URL;
    funnelPage.slug = '';

    funnelPage.positionX = FunnelFormComponent.FUNNEL_PAGE_WIDTH / 2 + 20;
    funnelPage.positionY = 20;

    this.pages.push(funnelPage);

    this.selectPage(funnelPage);
  }

  editUrl(): void {
    if (this.selectedPage) {
      const url = this.selectedPage.url;
      if (url) {
        this.dialogService.openDialog(FunnelUrlEditorComponent, {
          data: this.selectedPage,
          class: 'modal-lg'
        }).subscribe(() => {
        });
      }
    }
  }

  addLinkCondition(link: FunnelFormLink): void {
    if (this.linkConditionType) {
      let condition = new FunnelLinkCondition();
      condition.init(this.linkConditionType);
      link.link?.conditions.push(condition);
    }
  }

  removeLinkCondition(link: FunnelFormLink, condition: FunnelLinkCondition): void {
    const i = link.link?.conditions.indexOf(condition) ?? -1;
    if (i >= 0) {
      link.link?.conditions.splice(i, 1);
    }
  }

  save(): void {
    if (this.form.invalid) {
      return;
    }

    this.loader.startLoading();

    let params: any = {
      name: this.form.get('name')?.value,
      slug: this.form.get('slug')?.value,
      domainId: this.form.get('domainId')?.value,
      startPageId: this.form.get('startPage')?.value?.id,
      enabled: this.form.get('enabled')?.value,
      pages: []
    }

    this.pages.forEach((page: FunnelPage) => {
      const links: any = [];

      this.links.forEach((link: FunnelFormLink) => {
        if (link.previousPage?.id && link.nextPage?.id && link.previousPage?.id == page.id) {
          links.push({
            toId: link.nextPage?.id,
            conditions: link.link?.conditions ?? [],
          });
        }
      });

      params.pages.push({
        id: page.id,
        name: page.name,
        type: page.type,
        url: page.type === FunnelPageType.URL ? page.url : null,
        options: page.options,
        slug: page.slug,
        positionX: page.positionX,
        positionY: page.positionY,
        staticPageId: page.type === FunnelPageType.STATIC_PAGE ? page.staticPage?.id : null,
        links: links
      });
    });

    if (this.isCreate()) {
      this.funnelService.create(params).subscribe((response) => {
        this.loader.stopLoading();

        this.id = response.id;

        this.router.navigateByUrl('/promotion-tools/cms/funnels/' + this.id).then(() => {
          this.snackbar.success('Funnel has been created');
          this.refresh();
        });
      });
    } else {
      if (this.id) {
        this.funnelService.update(this.id, params).subscribe(() => {
          this.loader.stopLoading();

          this.router.navigateByUrl('/promotion-tools/cms/funnels/' + this.id).then(() => {
            this.snackbar.success('Funnel has been updated');

            this.refresh();
          });
        });
      }
    }
  }
}

interface FunnelFormLink {
  previousPage?: FunnelPage;
  nextPage?: FunnelPage;
  nextX?: number;
  nextY?: number;
  link?: FunnelLink;
}
